import { cn } from "@tcg/ui";
import { useRealtimeStatus } from "@tcg/realtime/react";
import { useLiveQuery } from "dexie-react-hooks";
import { CloudUpload, Info, Lock, RefreshCw, UserRoundCog, Wifi, WifiOff } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import { usePos } from "@/app/context";
import { LanguagePicker } from "@/components/LanguagePicker";
import { useConnectivity, type Connectivity } from "@/features/offline/connectivity";
import { OfflineDetailsSheet } from "@/features/offline/OfflineDetailsSheet";
import { PrintQueueBanner } from "@/features/receipt/PrintQueueBanner";
import { minutesSince } from "@/lib/snapshot";

export type Health = "ok" | "degraded" | "offline" | "offline_long";

export interface StatusBarProps {
  registerLabel: string | null;
  staffName: string | null;
  shiftSince: string | null;
  /** The browser's own online flag (the monitor decides the colour — T07-33). */
  online: boolean;
  onLock: () => void;
  onSwitch: () => void;
  updateReady?: boolean;
  onUpdate?: () => void;
  /** Gallery / tests: show this state instead of the monitor's. */
  connectivity?: Connectivity;
}

/**
 * Green = online + live updates; amber = online but updates are slow (every 30 s); yellow =
 * offline < 5 min (cash only); red = offline > 5 min (one-of-a-kind cards stop selling).
 */
export function healthOf(
  online: boolean,
  realtime: string,
  state: Connectivity = "online",
): Health {
  if (state === "offline_long") {
    return "offline_long";
  }
  if (!online || state === "offline_short" || realtime === "offline") {
    return "offline";
  }
  return realtime === "fallback" || realtime === "reconnecting" ? "degraded" : "ok";
}

/**
 * Top bar of every screen (T07-22 + T07-33, design/05 §3–4): the connection as a colour + word
 * (+ 「詳細」 = what can / cannot be done now), how old the offline copy is, the offline queue and the
 * 要確認 badge (→ 同期状況), failed prints, register, staff, shift, the language picker, 「ロック」 and
 * 「交代」.
 */
export function StatusBar({
  registerLabel,
  staffName,
  shiftSince,
  online,
  onLock,
  onSwitch,
  updateReady,
  onUpdate,
  connectivity: forced,
}: StatusBarProps) {
  const { t } = useTranslation();
  const { db } = usePos();
  const realtime = useRealtimeStatus();
  const monitored = useConnectivity();
  const state =
    forced ??
    (online ? monitored.state : monitored.state === "online" ? "offline_short" : monitored.state);
  const health = healthOf(online, realtime, state);
  const offline = health === "offline" || health === "offline_long";
  const [details, setDetails] = useState(false);
  const queued = useLiveQuery(() => db.opQueue.count(), [db]) ?? 0;
  const reviews =
    useLiveQuery(
      () =>
        db.syncResults
          .where("needsReview")
          .equals(1)
          .filter((r) => !r.resolvedAt)
          .count(),
      [db],
    ) ?? 0;
  const meta = useLiveQuery(() => db.snapshotMeta.toCollection().last(), [db]);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 15_000);
    return () => clearInterval(timer);
  }, []);
  const copyMinutes = minutesSince(meta?.fetchedAt, now);

  return (
    <header className="flex min-h-12 flex-wrap items-center gap-x-3 gap-y-1 border-b bg-muted px-3 py-1 text-sm">
      <span
        role="status"
        data-health={health}
        className={cn(
          "flex items-center gap-1.5 rounded-full px-3 py-1 font-semibold",
          health === "ok" && "bg-pos-ok text-white",
          health === "degraded" && "bg-status-pending text-black",
          health === "offline" && "bg-yellow-300 text-black",
          health === "offline_long" && "bg-status-problem text-white",
        )}
      >
        {offline ? (
          <WifiOff aria-hidden className="size-4" />
        ) : (
          <Wifi aria-hidden className="size-4" />
        )}
        {health === "offline_long"
          ? t("offline.status_long")
          : health === "offline"
            ? t("offline.status_short")
            : health === "ok"
              ? t("status.online")
              : t("status.slow")}
      </span>
      {offline ? (
        <>
          <span className="font-medium">
            {copyMinutes === null
              ? t("offline.copy_none")
              : t("offline.copy_age", { minutes: copyMinutes })}
          </span>
          <button
            type="button"
            onClick={() => setDetails(true)}
            className="flex min-h-10 items-center gap-1 rounded-md border bg-background px-3 font-medium"
          >
            <Info aria-hidden className="size-4" />
            {t("offline.details")}
          </button>
        </>
      ) : null}
      {queued > 0 || reviews > 0 ? (
        <Link
          to="/sync"
          className={cn(
            "flex min-h-10 items-center gap-1 rounded-md border px-3 font-medium",
            reviews > 0 ? "border-status-problem bg-status-problem text-white" : "bg-background",
          )}
        >
          <CloudUpload aria-hidden className="size-4" />
          {reviews > 0
            ? t("sync.badge_review", { count: reviews, queued })
            : t("sync.badge_queued", { count: queued })}
        </Link>
      ) : null}
      {registerLabel ? (
        <span>
          {t("status.register")}: <strong>{registerLabel}</strong>
        </span>
      ) : null}
      <span>
        {shiftSince ? t("status.shift_open", { time: shiftSince }) : t("status.shift_none")}
      </span>
      {staffName ? (
        <span>
          {t("status.staff")}: <strong>{staffName}</strong>
        </span>
      ) : null}
      <span className="ml-auto flex items-center gap-2">
        <PrintQueueBanner />
        <LanguagePicker />
        {updateReady && onUpdate ? (
          <button
            type="button"
            onClick={onUpdate}
            title={t("app.update_available")}
            className="flex min-h-10 items-center gap-1 rounded-md border bg-background px-3"
          >
            <RefreshCw aria-hidden className="size-4" />
            {t("app.reload")}
          </button>
        ) : null}
        <button
          type="button"
          onClick={onSwitch}
          className="flex min-h-10 items-center gap-1 rounded-md border bg-background px-3"
        >
          <UserRoundCog aria-hidden className="size-4" />
          {t("app.sign_out")}
        </button>
        <button
          type="button"
          onClick={onLock}
          className="flex min-h-10 items-center gap-1 rounded-md border bg-background px-3"
        >
          <Lock aria-hidden className="size-4" />
          {t("app.lock")}
        </button>
      </span>
      <OfflineDetailsSheet open={details} onClose={() => setDetails(false)} state={state} />
    </header>
  );
}
